import React, { useState } from 'react';

const fmt = n => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const prettyFeeType = t => String(t || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

/**
 * Small "i" info icon shown next to a pending amount. Clicking it opens a
 * popup listing what makes up the pending total (from the pendingBreakdown
 * returned by /admin/reports/pending-fees).
 */
function PendingInfoIcon({ studentName, totalPending, breakdown }) {
    const [open, setOpen] = useState(false);

    if (!breakdown || breakdown.length === 0) return null;

    return (
        <>
            <button
                onClick={e => { e.stopPropagation(); setOpen(true); }}
                title="View pending amount details"
                aria-label="View pending amount details"
                style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    border: '1px solid var(--border-color)', background: 'var(--surface)',
                    color: 'var(--text-muted)', fontSize: 11, fontWeight: 700,
                    fontFamily: 'Georgia, serif', fontStyle: 'italic', lineHeight: 1,
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    padding: 0,
                }}
            >
                i
            </button>

            {open && (
                <div
                    onClick={e => { e.stopPropagation(); setOpen(false); }}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                        zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 16,
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: 'var(--surface, #fff)', borderRadius: 12, width: '100%', maxWidth: 420,
                            maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
                        }}
                    >
                        <div style={{
                            padding: '16px 20px', borderBottom: '1px solid var(--border-soft)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                        }}>
                            <div>
                                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                                    Pending details
                                </div>
                                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--forest-900)', marginTop: 2 }}>
                                    {studentName}
                                </div>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                aria-label="Close"
                                style={{ border: 'none', background: 'none', fontSize: 22, color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1, padding: 4 }}
                            >
                                &times;
                            </button>
                        </div>

                        <div style={{ padding: '8px 20px' }}>
                            {breakdown.map((item, i) => (
                                <div key={i} style={{
                                    padding: '12px 0',
                                    borderBottom: i < breakdown.length - 1 ? '1px solid var(--border-soft)' : 'none',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                                        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--forest-900)' }}>
                                            {item.frequency === 'ITEM' ? (item.itemName || 'Other charge') : prettyFeeType(item.feeType)}
                                        </span>
                                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--error-color)', whiteSpace: 'nowrap' }}>
                                            {fmt(item.pendingAmount)}
                                        </span>
                                    </div>

                                    {item.discount > 0 && (
                                        <div style={{ fontSize: 12, color: 'var(--success-color)', marginTop: 3 }}>
                                            {fmt(item.structureAmount)} − {fmt(item.discount)} discount = {fmt(item.effectiveAmount)}
                                            {item.frequency === 'MONTHLY' ? ' / month' : ''}
                                        </div>
                                    )}

                                    {item.frequency === 'MONTHLY' && item.months?.length > 0 && (
                                        <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--text-muted)' }}>
                                            {item.months.map((m, idx) => <li key={idx}>{m}</li>)}
                                        </ul>
                                    )}

                                    {item.frequency === 'ONE_TIME' && item.paidAmount > 0 && (
                                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                                            Paid so far: {fmt(item.paidAmount)}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div style={{
                            padding: '14px 20px', borderTop: '1px solid var(--border-soft)', background: 'var(--cream-50, #faf7f2)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-light)' }}>Total pending</span>
                            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--error-color)' }}>{fmt(totalPending)}</span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default PendingInfoIcon;
